import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import { formatHelper } from "helper/format";
import PendingActionBadge from "components/allInOneDashboard/PendingActionBadge";
import ProductCardIcon from "components/allInOneDashboard/ProductCardIcon";

const PILL_SX = {
  gain: {
    color: "#00E676",
    bgcolor: "#132C33",
    border: "1px solid rgba(0,230,118,0.49)",
  },
  loss: {
    color: "#FF6B6B",
    bgcolor: "#2C1313",
    border: "1px solid rgba(255,107,107,0.49)",
  },
};

const EMPTY_COUNTDOWN = { days: 0, hours: 0, minutes: 0, seconds: 0 };

const parseRevealTargetMs = (revealTime) => {
  if (!revealTime) return null;
  const ms = new Date(
    `${revealTime}`.toUpperCase().replace("Z", ""),
  ).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const getCountdownParts = (targetMs) => {
  if (!targetMs) return EMPTY_COUNTDOWN;
  const diff = targetMs - Date.now();
  if (diff <= 0) return EMPTY_COUNTDOWN;

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
};

const buildPhcStatsFromPortfolio = (portfolio) => {
  if (!portfolio) return [];
  const returnsValue = portfolio.returns ?? portfolio.return ?? null;
  const returnsLabel =
    returnsValue == null
      ? "—"
      : String(returnsValue).includes("%")
        ? String(returnsValue)
        : `${returnsValue}%`;
  const numeric = parseFloat(String(returnsLabel).replace(/[^\d.-]/g, ""));
  const tone =
    Number.isNaN(numeric) || numeric === 0
      ? "neutral"
      : numeric > 0
        ? "gain"
        : "loss";

  return [
    { label: "No of Stocks", value: String(portfolio.no_of_stocks ?? "—") },
    {
      label: "Portfolio Beta",
      value: String(portfolio.portfolio_beta ?? "—"),
    },
    { label: "Returns", value: returnsLabel, tone },
  ];
};

const formatStockDateDisplay = (dateValue) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildOneStockStatsFromStock = (stock) => {
  if (!stock) return [];
  const buyPrice =
    stock.buy_price != null
      ? formatHelper.currency(stock.buy_price)
      : stock.buy_price_label || "—";
  return [
    { label: "Buy Price", value: buyPrice },
    {
      label: "Date",
      value: formatStockDateDisplay(stock.buy_date ?? stock.date),
    },
  ];
};

const StatBox = ({ label, value, tone = "neutral", compact = false }) => {
  const isPill = tone === "gain" || tone === "loss";

  return (
    <Box
      sx={{
        minWidth: 0,
        width: "100%",
        minHeight: compact ? 48 : 54,
        px: compact ? 0.5 : 1,
        py: compact ? 1 : 1.25,
        borderRadius: "12px",
        border: "0.6px solid #393939",
        bgcolor: "rgba(255,255,255,0.92)",
        boxShadow:
          "0px 1.25px 2.5px -1.25px rgba(0,0,0,0.15), 0px 2.5px 3.75px -0.6px rgba(0,0,0,0.08)",
        textAlign: "center",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
      }}
    >
      <Text
        sx={{
          color: "#A5A5A5",
          fontSize: compact ? "0.5625rem" : "0.625rem",
          lineHeight: 1.3,
          display: "block",
        }}
      >
        {label}
      </Text>
      {isPill ? (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 0.75,
            py: 0.35,
            borderRadius: "999px",
            fontSize: compact ? "0.625rem" : "0.6875rem",
            fontWeight: 700,
            ...PILL_SX[tone],
          }}
        >
          {value ?? "—"}
        </Box>
      ) : (
        <Text
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: compact ? "0.8125rem" : "0.9375rem",
            lineHeight: 1,
            wordBreak: "break-word",
          }}
        >
          {value ?? "—"}
        </Text>
      )}
    </Box>
  );
};

const SwitchChip = ({ onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 28,
      height: 28,
      borderRadius: "8px",
      border: "1px solid #E5E7EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      bgcolor: "#fff",
    }}
  >
    {children}
  </Box>
);

const CountdownBlock = ({ value, label }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: 0.5,
      py: 1,
      borderRadius: "10px",
      border: "0.6px solid #393939",
      bgcolor: "rgba(255,255,255,0.92)",
      boxShadow:
        "0px 1.25px 2.5px -1.25px rgba(0,0,0,0.15), 0px 2.5px 3.75px -0.6px rgba(0,0,0,0.08)",
      textAlign: "center",
    }}
  >
    <Text
      sx={{
        fontWeight: 700,
        color: "#111827",
        fontSize: "0.9375rem",
        lineHeight: 1.1,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {formatHelper.zeroPrefix(value)}
    </Text>
    <Text
      sx={{
        mt: 0.35,
        color: "#A5A5A5",
        fontSize: "0.5625rem",
        lineHeight: 1.2,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </Text>
  </Box>
);

const AnimatedContent = ({
  children,
  animationKey,
  direction = "forward",
  reducedMotion = false,
}) => (
  <Box
    key={animationKey}
    sx={
      reducedMotion
        ? undefined
        : {
            animation: `${
              direction === "backward"
                ? "unlockedCardSlideBackward"
                : "unlockedCardSlideForward"
            } 220ms ease-out`,
            "@keyframes unlockedCardSlideForward": {
              "0%": {
                opacity: 0,
                transform: "translateY(8px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
            "@keyframes unlockedCardSlideBackward": {
              "0%": {
                opacity: 0,
                transform: "translateY(-8px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }
    }
  >
    {children}
  </Box>
);

const OneStockWaitingPanel = ({ revealTime }) => {
  const targetMs = useMemo(() => parseRevealTargetMs(revealTime), [revealTime]);
  const [countdown, setCountdown] = useState(() => getCountdownParts(targetMs));

  useEffect(() => {
    if (!targetMs) {
      setCountdown(EMPTY_COUNTDOWN);
      return undefined;
    }

    setCountdown(getCountdownParts(targetMs));
    const timerId = setInterval(() => {
      setCountdown(getCountdownParts(targetMs));
    }, 1000);

    return () => clearInterval(timerId);
  }, [targetMs]);

  if (!targetMs) {
    return (
      <Box
        sx={{
          mt: "auto",
          pt: 1.5,
          px: 1.25,
          py: 1.5,
          borderRadius: "12px",
          border: "0.6px solid #E5E7EB",
          bgcolor: "rgba(255,255,255,0.92)",
        }}
      >
        <Text
          sx={{
            fontSize: "0.8125rem",
            color: "#6B7280",
            fontWeight: 500,
            lineHeight: 1.35,
          }}
        >
          We’ll suggest a stock when conditions fit your risk.
        </Text>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: "auto", pt: 1.5 }}>
      <Text
        sx={{
          mb: 1,
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: "#6B7280",
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        Next reveal
      </Text>
      <Stack direction="row" spacing={0.75}>
        <CountdownBlock value={countdown.days} label="Days" />
        <CountdownBlock value={countdown.hours} label="Hrs" />
        <CountdownBlock value={countdown.minutes} label="Min" />
        <CountdownBlock value={countdown.seconds} label="Sec" />
      </Stack>
    </Box>
  );
};

const UnlockedProductCard = ({
  product,
  onClick,
  variant = "default",
  hasPendingCall = false,
  pendingCount = 1,
}) => {
  const portfolioData = product.portfolioData || [];
  const stockData = product.stockData || [];
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isPhc = product.code === "PHC" || product.id === "phc";
  const isOneStock = product.code === "ONESTOCK" || product.id === "onestock";
  const isProtect = product.code === "RMS" || product.id === "protect";
  const [portfolioIndex, setPortfolioIndex] = useState(0);
  const [stockIndex, setStockIndex] = useState(0);
  const [detailDirection, setDetailDirection] = useState("forward");

  const isOneStockWaiting =
    isOneStock &&
    (!stockData.length ||
      product.revealStatus === "No Stocks" ||
      String(product.revealStatus || "").toLowerCase() === "no stocks");

  const safeIndex =
    portfolioData.length > 0
      ? Math.min(portfolioIndex, portfolioData.length - 1)
      : 0;

  const safeStockIndex =
    stockData.length > 0 ? Math.min(stockIndex, stockData.length - 1) : 0;

  const displayStats = useMemo(() => {
    if (isOneStockWaiting) return [];
    if (isOneStock && stockData.length > 0) {
      return buildOneStockStatsFromStock(stockData[safeStockIndex]);
    }
    if (isPhc && portfolioData.length > 0) {
      return buildPhcStatsFromPortfolio(portfolioData[safeIndex]);
    }
    return product.stats || [];
  }, [
    isOneStockWaiting,
    isOneStock,
    stockData,
    safeStockIndex,
    isPhc,
    portfolioData,
    safeIndex,
    product.stats,
  ]);

  const selectedStock =
    isOneStock && !isOneStockWaiting && stockData[safeStockIndex]
      ? stockData[safeStockIndex]
      : null;

  const selectedPortfolioName =
    isPhc && portfolioData[safeIndex]
      ? portfolioData[safeIndex].portfolio_name
      : null;

  const oneStockStatusFromReveal = (() => {
    if (!isOneStock || isOneStockWaiting) return null;
    const raw = product.revealStatus;
    if (!raw || raw === "No Stocks") {
      return product.monitorLabel || "Stock of the Day";
    }
    if (String(raw).toUpperCase() === "ACTIVATED") {
      return "Stock of the Day";
    }
    return raw;
  })();

  const statusLabel = isOneStockWaiting
    ? "Scanning for ideas"
    : isOneStock
      ? oneStockStatusFromReveal
      : product.revealStatus && product.revealStatus !== "No Stocks"
        ? product.revealStatus
        : product.monitorLabel || (isPhc ? "Portfolio Report" : null);

  const detailLabel = isOneStockWaiting
    ? null
    : selectedPortfolioName ||
      selectedStock?.stock_name ||
      product.stockSubtitle ||
      product.planName ||
      null;

  const isGold = variant === "privateWealth" || product.theme === "gold";
  const showPhcSwitcher = isPhc && portfolioData.length > 1;
  const showOneStockSwitcher =
    isOneStock && !isOneStockWaiting && stockData.length > 1;
  const showDetailSwitcher = showPhcSwitcher || showOneStockSwitcher;
  const ctaLabel =
    isOneStock && !isOneStockWaiting ? "Invest Now" : "View Dashboard";
  const productTitle =
    product.name?.replace(/^Jarvis\s+/i, "") || product.name;
  const infoTooltip =
    (isOneStockWaiting && product.revealNote) ||
    product.infoDescription ||
    product.revealNote ||
    "";

  const goPrevPortfolio = (event) => {
    event.stopPropagation();
    setDetailDirection("backward");
    setPortfolioIndex((prev) =>
      prev <= 0 ? portfolioData.length - 1 : prev - 1,
    );
  };

  const goNextPortfolio = (event) => {
    event.stopPropagation();
    setDetailDirection("forward");
    setPortfolioIndex((prev) =>
      prev >= portfolioData.length - 1 ? 0 : prev + 1,
    );
  };

  const goPrevStock = (event) => {
    event.stopPropagation();
    setDetailDirection("backward");
    setStockIndex((prev) => (prev <= 0 ? stockData.length - 1 : prev - 1));
  };

  const goNextStock = (event) => {
    event.stopPropagation();
    setDetailDirection("forward");
    setStockIndex((prev) => (prev >= stockData.length - 1 ? 0 : prev + 1));
  };

  const stockDetailTooltip =
    selectedStock?.stock_name ||
    selectedStock?.stock_symbol ||
    detailLabel ||
    "";
  const activeDetailKey = showPhcSwitcher
    ? `phc-${safeIndex}`
    : showOneStockSwitcher
      ? `stock-${safeStockIndex}`
      : "static";

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "39px",
        border: isGold ? "1px solid rgba(255,255,255,0.12)" : "1.6px solid #E5E7EB",
        bgcolor: isGold ? "transparent" : "#fff",
        background: isGold
          ? "linear-gradient(180deg, #7E5702 0%, #402C02 100%)"
          : "#fff",
        overflow: "hidden",
        backdropFilter: "blur(16px)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
        height: "100%",
        minHeight: { xs: 280, md: 300 },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        },
      }}
    >
      {!isGold && product.gradient ? (
        <Box
          sx={{
            position: "absolute",
            left: -2,
            top: -2,
            width: "100%",
            height: "100%",
            background: product.gradient,
            filter: "blur(19px)",
            pointerEvents: "none",
          }}
        />
      ) : null}

      <Stack
        sx={{
          p: { xs: 2, md: 2.25 },
          flex: 1,
          position: "relative",
          zIndex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <ProductCardIcon
              product={product}
              sx={{
                width: 34,
                height: 34,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
            <Text
              sx={{
                fontWeight: 700,
                fontSize: "clamp(1.05rem, 0.95rem + 0.35vw, 1.25rem)",
                color: isGold ? "#fff" : "#111827",
                lineHeight: 1.2,
              }}
            >
              {productTitle}
            </Text>
          </Stack>

          {hasPendingCall ? (
            <PendingActionBadge
              actionCentrePath={product.actionCentrePath}
              pendingCount={pendingCount}
            />
          ) : infoTooltip ? (
            <Tooltip title={infoTooltip} arrow placement="top">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(11px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src="/images/product-info/alert-circle.svg"
                  alt=""
                  sx={{ width: 20, height: 20 }}
                />
              </Box>
            </Tooltip>
          ) : null}
        </Stack>

        {product.riskProfile && !isOneStockWaiting ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 1.75 }}
          >
            <Box
              component="img"
              src="/images/product-info/risk-gauge.svg"
              alt=""
              sx={{ width: 19, height: 17 }}
            />
            <Text
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                color: isGold ? "#F9E8C0" : "#111827",
                letterSpacing: "-0.03em",
              }}
            >
              {product.riskProfile}
            </Text>
          </Stack>
        ) : null}

        {statusLabel && (!product.riskProfile || isOneStockWaiting) ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 1.75 }}
          >
            {isOneStock ? (
              <Box
                component="img"
                src="/images/product-info/stock-market.svg"
                alt=""
                sx={{ width: 15, height: 15 }}
              />
            ) : null}
            <Text
              sx={{
                fontSize: isOneStock ? "1.125rem" : "0.9375rem",
                fontWeight: 700,
                color: isOneStockWaiting
                  ? "#6B7280"
                  : isOneStock
                    ? "#00E676"
                    : isGold
                      ? "#F9E8C0"
                      : "#111827",
                letterSpacing: "-0.03em",
              }}
            >
              {statusLabel}
            </Text>
          </Stack>
        ) : null}

        {isProtect && product.monitorLabel && product.riskProfile ? (
          <Text
            sx={{
              mt: 0.75,
              fontSize: "0.9375rem",
              fontWeight: 700,
              color: isGold ? "#F9E8C0" : "#111827",
            }}
          >
            {product.monitorLabel}
          </Text>
        ) : null}

        {showDetailSwitcher ? (
          <AnimatedContent
            animationKey={activeDetailKey}
            direction={detailDirection}
            reducedMotion={prefersReducedMotion}
          >
            {detailLabel ? (
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Tooltip title={stockDetailTooltip} arrow placement="top">
                    <Text
                      sx={{
                        fontSize: "0.875rem",
                        color: isGold ? "rgba(255,255,255,0.85)" : "#111827",
                        fontWeight: 600,
                        pr: 1,
                        lineHeight: 1.2,
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {detailLabel}
                    </Text>
                  </Tooltip>
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <SwitchChip
                      onClick={showPhcSwitcher ? goPrevPortfolio : goPrevStock}
                    >
                      <KeyboardArrowUpIcon
                        sx={{ fontSize: 18, color: "#374151" }}
                      />
                    </SwitchChip>
                    <SwitchChip
                      onClick={showPhcSwitcher ? goNextPortfolio : goNextStock}
                    >
                      <KeyboardArrowDownIcon
                        sx={{ fontSize: 18, color: "#374151" }}
                      />
                    </SwitchChip>
                  </Stack>
                </Stack>
                {showOneStockSwitcher ? (
                  <Text
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: isGold ? "rgba(255,255,255,0.65)" : "#6B7280",
                    }}
                  >
                    {safeStockIndex + 1} of {stockData.length}
                  </Text>
                ) : null}
              </Stack>
            ) : null}

            {isOneStockWaiting ? (
              <OneStockWaitingPanel revealTime={product.revealTime} />
            ) : displayStats.length > 0 ? (
              <Box
                sx={{
                  mt: "auto",
                  pt: 1.5,
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns:
                    displayStats.length === 3
                      ? "repeat(3, minmax(0, 1fr))"
                      : "repeat(2, minmax(0, 1fr))",
                }}
              >
                {displayStats.map((stat) => (
                  <StatBox
                    key={`${stat.label}-${safeStockIndex}-${stat.value}`}
                    label={stat.label}
                    value={stat.value}
                    tone={stat.tone}
                    compact={displayStats.length === 3}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
          </AnimatedContent>
        ) : (
          <>
            {detailLabel ? (
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Tooltip title={stockDetailTooltip} arrow placement="top">
                    <Text
                      sx={{
                        fontSize: "0.875rem",
                        color: isGold ? "rgba(255,255,255,0.85)" : "#111827",
                        fontWeight: 600,
                        pr: 1,
                        lineHeight: 1.2,
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {detailLabel}
                    </Text>
                  </Tooltip>
                </Stack>
              </Stack>
            ) : null}

            {isOneStockWaiting ? (
              <OneStockWaitingPanel revealTime={product.revealTime} />
            ) : displayStats.length > 0 ? (
              <Box
                sx={{
                  mt: "auto",
                  pt: 1.5,
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns:
                    displayStats.length === 3
                      ? "repeat(3, minmax(0, 1fr))"
                      : "repeat(2, minmax(0, 1fr))",
                }}
              >
                {displayStats.map((stat) => (
                  <StatBox
                    key={`${stat.label}-${safeStockIndex}-${stat.value}`}
                    label={stat.label}
                    value={stat.value}
                    tone={stat.tone}
                    compact={displayStats.length === 3}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
          </>
        )}
      </Stack>

      <Box
        onClick={onClick}
        sx={{
          mx: 1.5,
          mb: 1.5,
          height: 42,
          borderRadius: "12px",
          border: isGold ? "1px solid rgba(255,255,255,0.25)" : "1px solid #2563EB",
          background: isGold
            ? "rgba(255,255,255,0.15)"
            : "linear-gradient(165deg, #1D4ED8 0%, #2563EB 100%)",
          boxShadow: isGold
            ? "none"
            : "0px 10px 24px -8px rgba(29,78,216,0.2)",
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          position: "relative",
          zIndex: 1,
          "&:hover": {
            filter: "brightness(1.05)",
          },
        }}
      >
        <Text sx={{ fontWeight: 500, fontSize: "0.875rem", color: "#fff" }}>
          {ctaLabel}
        </Text>
        <ArrowForwardIcon sx={{ fontSize: 18, color: "#fff" }} />
      </Box>
    </Box>
  );
};

export default UnlockedProductCard;
